"use client";

import { Button, CopyButton, Group, Paper, Text } from "@mantine/core";
import { IconCheck, IconCopy } from "@tabler/icons-react";

interface CopyPillProps {
  /** The exact string the button puts on the clipboard; also what the pill shows. */
  value: string;
  /** The string's size. `xs` keeps the string and its button on one line in
      a narrow card whose own text is small, as on the homepage's close. */
  size?: "xs" | "sm";
}

/** A technical string a person is meant to paste somewhere else, shown in mono
    with a Copy button. The same pill wherever a URL or a one-line command
    appears on the marketing site, so it reads as the same artifact each time. */
export default function CopyPill({ value, size = "sm" }: CopyPillProps) {
  return (
    <Paper withBorder radius="xl" bg="gray.0" px="md" py="2xs" w="fit-content" maw="100%">
      {/* The row wraps so a narrow screen drops the button under the text
          instead of squeezing it. The URL stays whole unless the pill itself
          is narrower than the URL; "anywhere", not "break-word", because a
          flex item never shrinks below its min-content, which only
          "anywhere" lets a long URL reduce. */}
      <Group gap="sm">
        <Text ff="monospace" size={size} c="gray.8" style={{ overflowWrap: "anywhere" }}>
          {value}
        </Text>
        <CopyButton value={value}>
          {({ copied, copy }) => (
            <Button
              size="compact-sm"
              variant={copied ? "light" : "subtle"}
              color={copied ? "teal" : "blue"}
              onClick={copy}
              leftSection={copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
            >
              {copied ? "Copied" : "Copy"}
            </Button>
          )}
        </CopyButton>
      </Group>
    </Paper>
  );
}
